<template>
  <div class="consulta-inventario-container">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="page-title">Consulta de Inventario y Stock</h3>
          <p class="text-muted mb-0">Monitoreo en tiempo real del inventario y movimientos de productos</p>
        </div>
        <div class="col-auto">
          <button class="btn btn-outline-success me-2" @click="actualizarInventario">
            <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': isLoading }"></i>
            Actualizar
          </button>
          <button class="btn btn-primary" @click="exportarInventario">
            <i class="fas fa-file-excel me-2"></i>
            Exportar a Excel
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Cards -->
    <div class="row mb-4">
      <div class="col-xl-3 col-md-6">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Productos</h6>
                <h3 class="mb-0">{{ estadisticas.totalProductos }}</h3>
                <small class="text-success">
                  <i class="fas fa-arrow-up me-1"></i>
                  {{ estadisticas.productosMes }} este mes
                </small>
              </div>
              <div class="stat-icon bg-primary">
                <i class="fas fa-box"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Stock Total</h6>
                <h3 class="mb-0">{{ estadisticas.stockTotal.toLocaleString() }}</h3>
                <small class="text-muted">unidades disponibles</small>
              </div>
              <div class="stat-icon bg-success">
                <i class="fas fa-cubes"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card stat-card border-warning">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Bajo Stock</h6>
                <h3 class="mb-0 text-warning">{{ estadisticas.bajoStock }}</h3>
                <small class="text-warning">
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  requieren atención
                </small>
              </div>
              <div class="stat-icon bg-warning">
                <i class="fas fa-exclamation-circle"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-3 col-md-6">
        <div class="card stat-card border-danger">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Sin Stock</h6>
                <h3 class="mb-0 text-danger">{{ estadisticas.sinStock }}</h3>
                <small class="text-danger">
                  <i class="fas fa-times-circle me-1"></i>
                  productos agotados
                </small>
              </div>
              <div class="stat-icon bg-danger">
                <i class="fas fa-box-open"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alertas de Bajo Stock -->
    <div class="row mb-4" v-if="productosBajoStock.length > 0">
      <div class="col-12">
        <div class="alert alert-warning alert-dismissible fade show" role="alert">
          <div class="d-flex align-items-center">
            <i class="fas fa-exclamation-triangle fa-2x me-3"></i>
            <div>
              <h5 class="alert-heading mb-1">Alerta de Inventario</h5>
              <p class="mb-0">
                Tienes <strong>{{ productosBajoStock.length }}</strong> producto(s) con bajo stock.
                <a href="#tabla-inventario" class="alert-link">Ver detalles</a>
              </p>
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      </div>
    </div>

    <!-- Filtros Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-filter me-2"></i>
              Filtros de Búsqueda
            </h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- Búsqueda -->
              <div class="col-md-4">
                <label class="form-label">Buscar Producto</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="fas fa-search"></i>
                  </span>
                  <input type="text" v-model="filtros.busqueda" class="form-control"
                    placeholder="Nombre o código del producto..." @input="aplicarFiltros" />
                </div>
              </div>

              <!-- Categoría -->
              <div class="col-md-3">
                <label class="form-label">Categoría</label>
                <select v-model="filtros.categoria" class="form-select" @change="aplicarFiltros">
                  <option value="">Todas las categorías</option>
                  <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                    {{ cat.nombre }}
                  </option>
                </select>
              </div>

              <!-- Estado de Stock -->
              <div class="col-md-3">
                <label class="form-label">Estado de Stock</label>
                <select v-model="filtros.estadoStock" class="form-select" @change="aplicarFiltros">
                  <option value="">Todos</option>
                  <option value="disponible">Disponible</option>
                  <option value="bajo">Bajo Stock</option>
                  <option value="agotado">Agotado</option>
                </select>
              </div>

              <!-- Ordenar por -->
              <div class="col-md-2">
                <label class="form-label">Ordenar por</label>
                <select v-model="filtros.ordenar" class="form-select" @change="aplicarFiltros">
                  <option value="nombre">Nombre</option>
                  <option value="stock_asc">Stock (Menor)</option>
                  <option value="stock_desc">Stock (Mayor)</option>
                  <option value="precio">Precio</option>
                  <option value="ventas">Más Vendidos</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Inventario -->
    <div class="row" id="tabla-inventario">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col">
                <h5 class="card-title mb-0">
                  <i class="fas fa-list me-2"></i>
                  Inventario de Productos ({{ productosFiltrados.length }})
                </h5>
              </div>
              <div class="col-auto">
                <div class="btn-group" role="group">
                  <button type="button" class="btn btn-sm"
                    :class="vistaTabla === 'grid' ? 'btn-primary' : 'btn-outline-primary'" @click="vistaTabla = 'grid'">
                    <i class="fas fa-th"></i>
                  </button>
                  <button type="button" class="btn btn-sm"
                    :class="vistaTabla === 'table' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="vistaTabla = 'table'">
                    <i class="fas fa-list"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body">
            <!-- Vista de Tabla -->
            <div v-if="vistaTabla === 'table'" class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th width="60">Imagen</th>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Precio</th>
                    <th class="text-center">Stock Actual</th>
                    <th class="text-center">Vendidos</th>
                    <th class="text-center">Estado</th>
                    <th class="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="productosFiltrados.length === 0">
                    <td colspan="8" class="text-center py-5">
                      <i class="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                      <p class="text-muted">No se encontraron productos</p>
                    </td>
                  </tr>
                  <tr v-for="producto in productosPaginados" :key="producto.id" :class="{
                    'table-danger': producto.stock === 0,
                    'table-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo
                  }">
                    <td class="text-center align-middle">
                      <img
                        :src="getImageUrl(producto.portada)"
                        alt="Producto"
                        class="img-thumbnail"
                        style="width: 50px; height: 50px; object-fit: cover;"
                      />
                    </td>
                    <td>
                      <strong>{{ producto.titulo }}</strong>
                      <br />
                      <small class="text-muted">SKU: {{ producto.sku || 'N/A' }}</small>
                    </td>
                    <td>
                      <span class="badge bg-info">{{ producto.categoria?.nombre || 'Sin categoría' }}</span>
                    </td>
                    <td>
                      <strong class="text-success">${{ Number(producto.precio).toFixed(2) }}</strong>
                    </td>
                    <td class="text-center">
                      <h5 class="mb-0" :class="{
                        'text-danger': producto.stock === 0,
                        'text-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo,
                        'text-success': producto.stock > producto.stock_minimo
                      }">
                        {{ producto.stock || 0 }}
                      </h5>
                      <small class="text-muted">Min: {{ producto.stock_minimo || 5 }}</small>
                    </td>
                    <td class="text-center">
                      <strong>{{ producto.vendidos || 0 }}</strong>
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="{
                        'bg-danger': producto.stock === 0,
                        'bg-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo,
                        'bg-success': producto.stock > producto.stock_minimo
                      }">
                        {{ getEstadoStock(producto) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <div class="btn-group btn-group-sm" role="group">
                        <button class="btn btn-outline-primary" @click="verDetalleProducto(producto)"
                          title="Ver Detalles">
                          <i class="fas fa-eye"></i>
                        </button>
                        <button class="btn btn-outline-success" @click="ajustarStock(producto)" title="Ajustar Stock">
                          <i class="fas fa-edit"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Vista de Grid -->
            <div v-if="vistaTabla === 'grid'" class="row g-3">
              <div v-if="productosFiltrados.length === 0" class="col-12 text-center py-5">
                <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p class="text-muted">No se encontraron productos</p>
              </div>
              <div v-for="producto in productosPaginados" :key="producto.id" class="col-md-6 col-lg-4 col-xl-3">
                <div class="card product-card h-100" :class="{
                  'border-danger': producto.stock === 0,
                  'border-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo
                }">
                  <div class="product-image-wrapper">
                    <img :src="getImageUrl(producto.portada)" class="card-img-top" :alt="producto.titulo" />
                    <span class="badge position-absolute top-0 end-0 m-2" :class="{
                      'bg-danger': producto.stock === 0,
                      'bg-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo,
                      'bg-success': producto.stock > producto.stock_minimo
                    }">
                      {{ getEstadoStock(producto) }}
                    </span>
                  </div>
                  <div class="card-body">
                    <h6 class="card-title text-truncate" :title="producto.titulo">
                      {{ producto.titulo }}
                    </h6>
                    <p class="text-muted mb-2">
                      <small>{{ producto.categoria?.nombre || 'Sin categoría' }}</small>
                    </p>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong class="text-success">${{ Number(producto.precio).toFixed(2) }}</strong>
                      <span class="badge bg-secondary">{{ producto.vendidos || 0 }} vendidos</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <small class="text-muted">Stock:</small>
                        <h5 class="mb-0 d-inline ms-1" :class="{
                          'text-danger': producto.stock === 0,
                          'text-warning': producto.stock > 0 && producto.stock <= producto.stock_minimo,
                          'text-success': producto.stock > producto.stock_minimo
                        }">
                          {{ producto.stock || 0 }}
                        </h5>
                      </div>
                      <button class="btn btn-sm btn-outline-primary" @click="ajustarStock(producto)">
                        <i class="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Paginación -->
            <div v-if="productosFiltrados.length > 0" class="d-flex justify-content-between align-items-center mt-4">
              <div>
                Mostrando {{ (paginaActual - 1) * itemsPorPagina + 1 }} a
                {{ Math.min(paginaActual * itemsPorPagina, productosFiltrados.length) }} de
                {{ productosFiltrados.length }} productos
              </div>
              <nav>
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: paginaActual === 1 }">
                    <a class="page-link" href="#" @click.prevent="cambiarPagina(paginaActual - 1)">
                      Anterior
                    </a>
                  </li>
                  <li v-for="pagina in totalPaginas" :key="pagina" class="page-item"
                    :class="{ active: paginaActual === pagina }">
                    <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">
                      {{ pagina }}
                    </a>
                  </li>
                  <li class="page-item" :class="{ disabled: paginaActual === totalPaginas }">
                    <a class="page-link" href="#" @click.prevent="cambiarPagina(paginaActual + 1)">
                      Siguiente
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import api from '@/api';
import Chart from 'chart.js/auto';

export default {
  name: 'ConsultaInventario',
  setup() {
    const isLoading = ref(false);
    const vistaTabla = ref('table');
    const paginaActual = ref(1);
    const itemsPorPagina = ref(20);

    // Datos
    const productos = ref([]);
    const categorias = ref([]);
    const chartVendidos = ref(null);
    let chartVendidosInstance = null;

    // Estadísticas
    const estadisticas = ref({
      totalProductos: 0,
      productosMes: 0,
      stockTotal: 0,
      bajoStock: 0,
      sinStock: 0
    });

    // Filtros
    const filtros = ref({
      busqueda: '',
      categoria: '',
      estadoStock: '',
      ordenar: 'nombre'
    });

    // Computed
    const productosFiltrados = computed(() => {
      let resultado = [...productos.value];

      // Filtro de búsqueda
      if (filtros.value.busqueda) {
        const busqueda = filtros.value.busqueda.toLowerCase();
        resultado = resultado.filter(p =>
          p.titulo.toLowerCase().includes(busqueda) ||
          (p.sku && p.sku.toLowerCase().includes(busqueda))
        );
      }

      // Filtro de categoría
      if (filtros.value.categoria) {
        resultado = resultado.filter(p => p.categoriaId === parseInt(filtros.value.categoria));
      }

      // Filtro de estado de stock
      if (filtros.value.estadoStock) {
        resultado = resultado.filter(p => {
          const stock = p.stock || 0;
          const minimo = p.stock_minimo || 5;

          if (filtros.value.estadoStock === 'agotado') {
            return stock === 0;
          } else if (filtros.value.estadoStock === 'bajo') {
            return stock > 0 && stock <= minimo;
          } else if (filtros.value.estadoStock === 'disponible') {
            return stock > minimo;
          }
          return true;
        });
      }

      // Ordenar
      switch (filtros.value.ordenar) {
        case 'nombre':
          resultado.sort((a, b) => a.titulo.localeCompare(b.titulo));
          break;
        case 'stock_asc':
          resultado.sort((a, b) => (a.stock || 0) - (b.stock || 0));
          break;
        case 'stock_desc':
          resultado.sort((a, b) => (b.stock || 0) - (a.stock || 0));
          break;
        case 'precio':
          resultado.sort((a, b) => (b.precio || 0) - (a.precio || 0));
          break;
        case 'ventas':
          resultado.sort((a, b) => (b.vendidos || 0) - (a.vendidos || 0));
          break;
      }

      return resultado;
    });

    const productosBajoStock = computed(() => {
      return productos.value.filter(p => {
        const stock = p.stock || 0;
        const minimo = p.stock_minimo || 5;
        return stock > 0 && stock <= minimo;
      });
    });

    const productosPaginados = computed(() => {
      const inicio = (paginaActual.value - 1) * itemsPorPagina.value;
      const fin = inicio + itemsPorPagina.value;
      return productosFiltrados.value.slice(inicio, fin);
    });

    const totalPaginas = computed(() => {
      return Math.ceil(productosFiltrados.value.length / itemsPorPagina.value);
    });

    // Datos de gráficas
    const chartDataVendidos = computed(() => {
      const top10 = [...productos.value]
        .sort((a, b) => (b.vendidos || 0) - (a.vendidos || 0))
        .slice(0, 10);

      return {
        labels: top10.map(p => p.titulo.length > 20 ? p.titulo.substring(0, 20) + '...' : p.titulo),
        data: top10.map(p => p.vendidos || 0)
      };
    });

    // Methods
    const fetchProductos = async () => {
      try {
        isLoading.value = true;
        const response = await api.get('/producto/buscar/');

        // Adaptar datos del backend (agregar campos calculados si no existen)
        productos.value = response.data.map(producto => ({
          ...producto,
          imagen: producto.portada, // Mapear portada a imagen
          stock_minimo: producto.stock_minimo || 5, // Valor por defecto si no existe
          vendidos: producto.vendidos || 0, // Valor por defecto si no existe
          sku: producto.slug // Usar slug como SKU temporalmente
        }));

        calcularEstadisticas();
      } catch (error) {
        console.error('Error al cargar productos:', error);
        window.Swal.fire({
          title: 'Error',
          text: 'No se pudieron cargar los productos',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const fetchCategorias = async () => {
      try {
        const response = await api.get('/categoria');
        categorias.value = response.data;
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };

    const calcularEstadisticas = () => {
      const ahora = new Date();
      const mesActual = ahora.getMonth();
      const anioActual = ahora.getFullYear();

      estadisticas.value.totalProductos = productos.value.length;
      estadisticas.value.stockTotal = productos.value.reduce((sum, p) => sum + (p.stock || 0), 0);
      estadisticas.value.sinStock = productos.value.filter(p => (p.stock || 0) === 0).length;
      estadisticas.value.bajoStock = productosBajoStock.value.length;

      // Productos del mes
      estadisticas.value.productosMes = productos.value.filter(p => {
        if (!p.createdAt) return false;
        const fecha = new Date(p.createdAt);
        return fecha.getMonth() === mesActual && fecha.getFullYear() === anioActual;
      }).length;
    };

    const aplicarFiltros = () => {
      paginaActual.value = 1;
    };

    const cambiarPagina = (pagina) => {
      if (pagina >= 1 && pagina <= totalPaginas.value) {
        paginaActual.value = pagina;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const getEstadoStock = (producto) => {
      const stock = producto.stock || 0;
      const minimo = producto.stock_minimo || 5;

      if (stock === 0) return 'Agotado';
      if (stock <= minimo) return 'Bajo Stock';
      return 'Disponible';
    };

    const actualizarInventario = () => {
      fetchProductos();
      window.Swal.fire({
        title: 'Actualizado',
        text: 'Inventario actualizado correctamente',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    };

    const exportarInventario = () => {
      window.Swal.fire({
        title: 'Exportar Inventario',
        text: 'Funcionalidad de exportación en desarrollo',
        icon: 'info',
        confirmButtonText: 'OK'
      });
    };

    const verDetalleProducto = (producto) => {
      window.Swal.fire({
        title: producto.titulo,
        html: `
          <div class="text-start">
            <p><strong>SKU:</strong> ${producto.sku || 'N/A'}</p>
            <p><strong>Categoría:</strong> ${producto.categoria?.nombre || 'N/A'}</p>
            <p><strong>Precio:</strong> $${Number(producto.precio).toFixed(2)}</p>
            <p><strong>Stock Actual:</strong> ${producto.stock || 0}</p>
            <p><strong>Stock Mínimo:</strong> ${producto.stock_minimo || 5}</p>
            <p><strong>Vendidos:</strong> ${producto.vendidos || 0}</p>
            <p><strong>Estado:</strong> ${getEstadoStock(producto)}</p>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Cerrar'
      });
    };

    const ajustarStock = async (producto) => {
      const { value: nuevoStock } = await window.Swal.fire({
        title: `Ajustar Stock: ${producto.titulo}`,
        html: `
          <p>Stock actual: <strong>${producto.stock || 0}</strong></p>
          <input id="swal-input-stock" class="swal2-input" type="number" placeholder="Nuevo stock" value="${producto.stock || 0}">
          <input id="swal-input-minimo" class="swal2-input" type="number" placeholder="Stock mínimo" value="${producto.stock_minimo || 5}">
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          return {
            stock: document.getElementById('swal-input-stock').value,
            stock_minimo: document.getElementById('swal-input-minimo').value
          };
        }
      });

      if (nuevoStock) {
        try {
          await api.put(`/producto/${producto.id}/stock`, {
            stock: parseInt(nuevoStock.stock),
            stock_minimo: parseInt(nuevoStock.stock_minimo)
          });

          window.Swal.fire({
            title: 'Éxito',
            text: 'Stock actualizado correctamente',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });

          fetchProductos();
        } catch (error) {
          console.error('Error al ajustar stock:', error);
          window.Swal.fire({
            title: 'Error',
            text: 'No se pudo actualizar el stock',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      }
    };

    const getImageUrl = (portada) => {
      
      // Obtener el nombre del archivo (último segmento del path)
      const fileName = portada.split('/').pop();

      // Usar la misma estructura que listProducts.vue
      const baseURL = api.defaults.baseURL;
      return `${baseURL}/producto/obtener_image_producto/${fileName}`;
    };

    // Lifecycle
    onMounted(async () => {
      await fetchProductos();
      await fetchCategorias();

    });

    onUnmounted(() => {
      if (chartVendidosInstance) {
        chartVendidosInstance.destroy();
      }
    });

    return {
      isLoading,
      vistaTabla,
      paginaActual,
      itemsPorPagina,
      productos,
      categorias,
      estadisticas,
      filtros,
      productosFiltrados,
      productosBajoStock,
      productosPaginados,
      totalPaginas,
      chartDataVendidos,
      chartVendidos,
      aplicarFiltros,
      cambiarPagina,
      getEstadoStock,
      actualizarInventario,
      exportarInventario,
      verDetalleProducto,
      ajustarStock,
      getImageUrl
    };
  }
};
</script>

<style scoped>
.consulta-inventario-container {
  padding: 0;
}

.page-header {
  margin-bottom: 1.5rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.stat-card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
}

.stat-card.border-warning {
  border-left: 4px solid #ffc107 !important;
}

.stat-card.border-danger {
  border-left: 4px solid #dc3545 !important;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.table thead th {
  border-top: none;
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.table-warning {
  background-color: #fff3cd !important;
}

.table-danger {
  background-color: #f8d7da !important;
}

.product-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
}

.product-card.border-warning {
  border: 2px solid #ffc107;
}

.product-card.border-danger {
  border: 2px solid #dc3545;
}

.product-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
  background-color: #f8f9fa;
}

.product-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.pagination {
  margin-bottom: 0;
}

.pagination .page-link {
  color: #667eea;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

.pagination .page-link:hover {
  background-color: #f8f9fa;
  color: #667eea;
}

@media (max-width: 768px) {
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }

  .product-image-wrapper {
    height: 150px;
  }
}
</style>
